import { Button, Tooltip, useConfirmationModal, useSnackbars } from '@customerio/pluma-components/react';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { useDeleteCollection } from '@/services/db/collection/mutations';
import type { Collection } from '@/services/db/collection/entity';

type DeleteCollectionButtonProps = {
  collection: Collection;
  workspaceId: number;
  canDelete: boolean;
  onDeleted?: () => void;
};

export function DeleteCollectionButton({ collection, workspaceId, canDelete, onDeleted }: DeleteCollectionButtonProps) {
  const taxonomy = useProductTaxonomy();
  const snackbars = useSnackbars();
  const confirm = useConfirmationModal();
  const deleteCollection = useDeleteCollection({ workspaceId });

  const handleDelete = async () => {
    const confirmed = await confirm({
      isDanger: true,
      title: 'Delete collection?',
      message: `This will permanently delete ${collection.name}. This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;
    try {
      await deleteCollection.mutateAsync({ id: collection.id });
      snackbars.create({ variant: 'success', message: `${collection.name} deleted.` });
      onDeleted?.();
    } catch {
      snackbars.create({ variant: 'error', message: "Collection couldn't be deleted. Try again." });
    }
  };

  return (
    <Tooltip
      content={`Collections that are used in a ${taxonomy.term('campaigns.singular')} cannot be deleted.`}
      placement="top"
      isDisabled={!collection.in_use}
      isCustom={true}
    >
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          variant="secondary"
          isDanger
          isDisabled={!canDelete || collection.importing || collection.in_use}
          onClick={() => void handleDelete()}
        >
          Delete collection
        </Button>
      )}
    </Tooltip>
  );
}
